import { PricingStrategy } from '@wix/ambassador-premium-domains-v1-recommendation/types';
import { Box, Heading, SkeletonRectangle, Text, Tooltip } from '@wix/design-system';
import { InfoCircleSmall } from '@wix/wix-ui-icons-common/odeditor';
import React from 'react';
import { clsx } from 'clsx';
import { DomainSearchGateModule } from '../../module/domainSearchGate.module';
import { useIsHarmonyTheme } from '@wix/core-domain-search';
import { GateApiClient } from '../../module/domainSearchGate.apiClient';
import { TldPromotionBoxDataHooks } from './dataHooks';
import s from './tldPromotionBoxes.scss';
import { DomainSearchSuiteModule } from '@wix/domains-search-suite';
import { usePremiumEssentialsContext } from '@wix/premium-fed-sdk/@premium-essentials';
const AI_TLD_TOOLTIP_KEY = 'domains-search-suite-translations-ns:domain_search_suite.tld_section.ai_tooltip.text';
const FIRST_YEAR_FREE_BADGE_KEY = 'domains_search_gate.gate_revamp.tld_card.badge.first_year_free';
const INTRO_OFFER_BADGE_KEY = 'domain_search_suite.business_email_results.intro_price.label';
const INTRO_PRICE_TOOLTIP_KEY = 'domains-search-suite-translations-ns:domains_search_suite.storefront.initial_vs_renewal.intro_price_tooltip';
const FREE_WITH_PLAN_BADGE_KEY = 'domain_search_suite.domain_gate.tld_section.badge.free_with_plan';
const FREE_YEAR_WITH_PLAN_TOOLTIP_KEY = 'domain_search_suite.domain_gate.suggestions.card.one_year_free.tooltip';
const FREE_YEAR_WITH_VOUCHER_TOOLTIP_KEY = 'domain_search_gate.results.free_for_one_year.use_voucher.tooltip';
export const TldPromotionBox = _ref => {
  let tld = _ref.tld,
    description = _ref.description,
    pricing = _ref.pricing,
    onClick = _ref.onClick,
    _ref$voucherAwarePric = _ref.voucherAwarePricing,
    voucherAwarePricing = _ref$voucherAwarePric === void 0 ? false : _ref$voucherAwarePric;
  const _DomainSearchSuiteMod = DomainSearchSuiteModule.internals.useTranslations(),
    tSuite = _DomainSearchSuiteMod.t;
  const _DomainSearchGateModu = DomainSearchGateModule.internals.useTranslations(),
    t = _DomainSearchGateModu.t;
  const isHarmonyTheme = useIsHarmonyTheme();
  const _usePremiumEssentials = usePremiumEssentialsContext(),
    siteId = _usePremiumEssentials.siteId;
  const _GateApiClient$hooks$ = GateApiClient.hooks.useTldsEligibleForVoucher({
      msid: siteId
    }, voucherAwarePricing),
    voucherState = _GateApiClient$hooks$.data;
  const isPremiumSite = Boolean(voucherState == null ? void 0 : voucherState.isPremiumSite);
  const showIntroPriceTooltip = voucherAwarePricing && Boolean(pricing == null ? void 0 : pricing.renewalPrice);
  const showFreeWithPlanBadge = (pricing == null ? void 0 : pricing.pricingStrategy) === PricingStrategy.FREE_FIRST_YEAR;
  const hasVoucherForTld = isPremiumSite && Boolean(voucherState == null ? void 0 : voucherState.hasValidVouchers) && Boolean(tld && (voucherState == null ? void 0 : voucherState.voucherSupportedTlds.includes(tld)));
  const showRevampBadge = voucherAwarePricing && (showFreeWithPlanBadge || Boolean(pricing == null ? void 0 : pricing.renewalPrice));
  const showFreeYearWithPlanTooltip = voucherAwarePricing && showFreeWithPlanBadge && !isPremiumSite;
  const showFreeYearWithVoucherTooltip = voucherAwarePricing && hasVoucherForTld;
  const showAIDisclaimer = tld === 'ai' && (pricing == null ? void 0 : pricing.pricingStrategy) === PricingStrategy.MULTI_YEAR_MINIMUM_COUNT;
  return /*#__PURE__*/React.createElement("div", {
    className: clsx(s.card, isHarmonyTheme && s.cardHarmony),
    onClick: onClick
  }, /*#__PURE__*/React.createElement(Box, {
    minHeight: "18px"
  }, (voucherAwarePricing ? showRevampBadge : showFreeWithPlanBadge) && /*#__PURE__*/React.createElement(Box, {
    dataHook: TldPromotionBoxDataHooks.badge,
    padding: "3px 6px",
    width: "max-content",
    backgroundColor: "#DFF994",
    borderRadius: "4px"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    weight: "normal",
    className: s.badgeText
  }, !voucherAwarePricing || !isPremiumSite ? tSuite(FREE_WITH_PLAN_BADGE_KEY) : hasVoucherForTld ? t(FIRST_YEAR_FREE_BADGE_KEY) : tSuite(INTRO_OFFER_BADGE_KEY)))), /*#__PURE__*/React.createElement(Heading, {
    size: "extraLarge",
    className: s.tld
  }, ".", tld), description && /*#__PURE__*/React.createElement(Text, {
    size: "small",
    secondary: true,
    className: s.description
  }, description), (pricing == null ? void 0 : pricing.price) && /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    gap: "SP1",
    verticalAlign: "middle",
    className: s.price,
    marginTop: "auto"
  }, pricing.originalPrice && /*#__PURE__*/React.createElement(Text, {
    size: "small",
    weight: "thin",
    secondary: true,
    light: true,
    className: s.strikethroughPrice
  }, t('domain_search_gate.domain_gate.tld_section.domain_price', {
    domainPrice: pricing.originalPrice
  })), /*#__PURE__*/React.createElement(Text, {
    size: "small",
    weight: "normal",
    className: clsx(voucherAwarePricing && s.reframedPrice)
  }, t('domain_search_gate.domain_gate.tld_section.domain_price', {
    domainPrice: pricing.price
  })), (showAIDisclaimer || showIntroPriceTooltip) && /*#__PURE__*/React.createElement(Tooltip, {
    content: /*#__PURE__*/React.createElement(Box, {
      padding: "SP3 SP4"
    }, /*#__PURE__*/React.createElement(Text, {
      size: "small",
      weight: "thin",
      light: true
    }, showAIDisclaimer ? t(AI_TLD_TOOLTIP_KEY) : showFreeYearWithPlanTooltip ? tSuite(FREE_YEAR_WITH_PLAN_TOOLTIP_KEY, {
      price: pricing.renewalPrice
    }) : showFreeYearWithVoucherTooltip ? t(FREE_YEAR_WITH_VOUCHER_TOOLTIP_KEY, {
      price: pricing.renewalPrice
    }) : t(INTRO_PRICE_TOOLTIP_KEY, {
      renewalPrice: pricing.renewalPrice
    })))
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(InfoCircleSmall, {
    className: s.infoIcon,
    "data-hook": TldPromotionBoxDataHooks.infoIcon
  })))));
};
export const TldPromotionBoxSkeleton = () => {
  const isHarmonyTheme = useIsHarmonyTheme();
  return /*#__PURE__*/React.createElement("div", {
    className: clsx(s.card, isHarmonyTheme && s.cardHarmony)
  }, /*#__PURE__*/React.createElement(SkeletonRectangle, {
    className: s.skeleton,
    width: "133px",
    height: "18px"
  }), /*#__PURE__*/React.createElement(SkeletonRectangle, {
    className: s.skeleton,
    width: "120px",
    height: "40px"
  }), /*#__PURE__*/React.createElement(SkeletonRectangle, {
    className: s.skeleton,
    width: "100%",
    height: "38px"
  }), /*#__PURE__*/React.createElement(SkeletonRectangle, {
    className: s.skeleton,
    width: "148px",
    height: "20px"
  }));
};
//# sourceMappingURL=TldPromotionBox.js.map